import { useState, useRef, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Play,
  SkipForward,
  Download,
  FileText,
  Terminal,
  RefreshCw,
  ArrowDownToLine,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useRun, useCancelRun, useSyncRun, useLogs, usePipelineLogs, useOutputs, useTenantSession } from '@/hooks/useTriplets'
import { useToast } from '@/hooks/use-toast'
import { getOutputDownloadUrl } from '@/lib/triplets-api'
import type {
  PipelineRunStatus,
  PipelineStepStatus,
  PipelineStep,
  OutputFile,
  TenantSession,
} from '@/types/triplets'

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const runStatusConfig: Record<
  PipelineRunStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }
> = {
  pending: { label: 'Pending', variant: 'outline' },
  running: { label: 'Running', variant: 'default', className: 'bg-blue-600 animate-pulse' },
  completed: { label: 'Completed', variant: 'default', className: 'bg-green-600' },
  failed: { label: 'Failed', variant: 'destructive' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
}

function RunStatusBadge({ status }: { status: PipelineRunStatus }) {
  const config = runStatusConfig[status] ?? { label: status, variant: 'outline' as const }
  return <Badge variant={config.variant} className={config.className}>{config.label}</Badge>
}

const stepIcons: Record<PipelineStepStatus, typeof CheckCircle2> = {
  pending: Clock,
  running: Play,
  completed: CheckCircle2,
  failed: XCircle,
  skipped: SkipForward,
}

const stepColors: Record<PipelineStepStatus, string> = {
  pending: 'text-muted-foreground',
  running: 'text-blue-500 animate-pulse',
  completed: 'text-green-500',
  failed: 'text-red-500',
  skipped: 'text-muted-foreground/50',
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function RunDetailPage() {
  const { runId } = useParams<{ runId: string }>()
  const { toast } = useToast()

  const [pollEnabled, setPollEnabled] = useState(true)

  const { data: run, isLoading: runLoading, error: runError } = useRun(runId ?? '', {
    refetchInterval: pollEnabled ? 3000 : false,
  })

  const isActive = run?.status === 'pending' || run?.status === 'running'

  // Stop polling once run reaches a terminal state
  useEffect(() => {
    if (run && !isActive) {
      setPollEnabled(false)
    }
  }, [run, isActive])

  const cancelRunMut = useCancelRun()
  const syncRunMut = useSyncRun()

  // REST logs — poll every 5s when active, single fetch otherwise
  const { data: restLogs, refetch: refetchLogs, isFetching: logsFetching } = useLogs(runId ?? '', {
    n: 5000,
    refetchInterval: isActive ? 5000 : false,
  })

  // WebSocket log streaming (only for active runs)
  const { lines: wsLines, done: logsDone } = usePipelineLogs(
    isActive ? runId : undefined,
  )

  // Merge: prefer WS lines if streaming, otherwise use REST
  const logLines = wsLines.length > 0 ? wsLines : (restLogs?.lines ?? [])

  // Outputs (fetch once run is not active)
  const { data: outputs } = useOutputs(runId ?? '', { enabled: !isActive })

  // Tenant session — fetch when run completes (config_name = tenant name)
  const isCompleted = run?.status === 'completed'
  const { data: session } = useTenantSession(
    isCompleted ? run?.config_name : undefined,
    { enabled: isCompleted },
  )

  const handleCancel = () => {
    cancelRunMut.mutate(runId!, {
      onSuccess: () => toast({ title: 'Pipeline run cancelled' }),
      onError: () => toast({ title: 'Failed to cancel', variant: 'destructive' }),
    })
  }

  const handleSync = () => {
    syncRunMut.mutate(runId!, {
      onSuccess: () => toast({ title: 'Steps synced' }),
      onError: () => toast({ title: 'Sync failed', variant: 'destructive' }),
    })
  }

  if (runLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (runError || !run) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-destructive">Pipeline run not found</h2>
          <p className="text-sm text-muted-foreground mt-2">
            {runError instanceof Error ? runError.message : `Run ID "${runId}" could not be loaded.`}
          </p>
          <Link to="/app/triplets">
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to pipelines
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const completedSteps = (run.steps ?? []).filter((s) => s.status === 'completed').length
  const totalSteps = (run.steps ?? []).length

  return (
    <div className="space-y-6">
      {/* Back link + header */}
      <div className="flex items-center gap-3">
        <Link to="/app/triplets">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{run.name}</h1>
            <RunStatusBadge status={run.status} />
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Config: {run.config_name}
            {run.created_at &&
              ` \u00b7 Created ${formatDistanceToNow(new Date(run.created_at), { addSuffix: true })}`}
            {run.total_time != null && ` \u00b7 ${run.total_time.toFixed(0)}s total`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isActive && (
            <Button variant="outline" size="sm" onClick={handleSync} disabled={syncRunMut.isPending}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Sync
            </Button>
          )}
          {isActive && (
            <Button variant="destructive" size="sm" onClick={handleCancel} disabled={cancelRunMut.isPending}>
              <XCircle className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Steps</div>
            <div className="text-2xl font-bold">{completedSteps}/{totalSteps}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Status</div>
            <div className="mt-1"><RunStatusBadge status={run.status} /></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Batch Job</div>
            <div className="text-sm font-mono truncate mt-1">{run.batch_job_id ?? '\u2014'}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Error</div>
            <div className="text-sm truncate mt-1 text-destructive">
              {run.error_message ?? '\u2014'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Steps timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pipeline Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {(run.steps ?? []).map((step, i) => (
              <StepChip key={step.name} step={step} isLast={i === (run.steps ?? []).length - 1} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Session info — shown after pipeline completes */}
      {session && <SessionCard session={session} />}

      {/* Tabs: Logs + Outputs */}
      <Tabs defaultValue="logs">
        <TabsList>
          <TabsTrigger value="logs">
            <Terminal className="h-4 w-4 mr-1" />
            Logs
          </TabsTrigger>
          <TabsTrigger value="outputs">
            <FileText className="h-4 w-4 mr-1" />
            Outputs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="mt-4">
          <LogViewer
            lines={logLines}
            isStreaming={isActive && !logsDone}
            onRefresh={() => refetchLogs()}
            isRefreshing={logsFetching}
          />
        </TabsContent>

        <TabsContent value="outputs" className="mt-4">
          <OutputsList outputs={outputs ?? []} runId={runId!} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Session card — RAGFlow details after pipeline completion
// ---------------------------------------------------------------------------

function SessionCard({ session }: { session: TenantSession }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">RAGFlow Session</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground text-xs mb-1">Knowledge Base Name</div>
            <div className="font-medium">{session.dataset_name || '\u2014'}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs mb-1">Knowledge Base ID</div>
            <div className="font-mono text-xs">{session.dataset_id || '\u2014'}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs mb-1">Documents</div>
            <div className="font-medium">{session.doc_count ?? '\u2014'}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs mb-1">Assistant Name</div>
            <div className="font-medium">{session.chat_name || '\u2014'}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs mb-1">Assistant ID (chat_id)</div>
            <div className="font-mono text-xs">{session.chat_id || '\u2014'}</div>
          </div>
          {session.chat_id_boost && (
            <div>
              <div className="text-muted-foreground text-xs mb-1">Boost Assistant ID</div>
              <div className="font-mono text-xs">{session.chat_id_boost}</div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Step chip
// ---------------------------------------------------------------------------

function StepChip({ step, isLast }: { step: PipelineStep; isLast: boolean }) {
  const Icon = stepIcons[step.status] ?? Clock
  const color = stepColors[step.status] ?? 'text-muted-foreground'

  return (
    <>
      <div
        className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm whitespace-nowrap ${
          step.status === 'running' ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : ''
        }`}
      >
        <Icon className={`h-4 w-4 ${color}`} />
        <span className="capitalize">{step.name}</span>
        {step.elapsed_seconds != null && (
          <span className="text-xs text-muted-foreground ml-1">
            {step.elapsed_seconds.toFixed(0)}s
          </span>
        )}
      </div>
      {!isLast && <span className="text-muted-foreground">&rarr;</span>}
    </>
  )
}

// ---------------------------------------------------------------------------
// Log viewer with refresh button and auto-scroll toggle
// ---------------------------------------------------------------------------

interface LogViewerProps {
  lines: string[]
  isStreaming: boolean
  onRefresh: () => void
  isRefreshing: boolean
}

function LogViewer({ lines, isStreaming, onRefresh, isRefreshing }: LogViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const [autoScroll, setAutoScroll] = useState(true)

  const scrollToBottom = useCallback(() => {
    if (autoScroll) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [autoScroll])

  useEffect(() => {
    scrollToBottom()
  }, [lines.length, scrollToBottom])

  // Detect if user scrolled up manually
  const handleScroll = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40
    setAutoScroll(atBottom)
  }, [])

  return (
    <div className="space-y-2">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <span className="text-xs text-muted-foreground">
            {lines.length} lines
          </span>
        </div>
        <Button
          variant={autoScroll ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            setAutoScroll(!autoScroll)
            if (!autoScroll) {
              bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
            }
          }}
        >
          <ArrowDownToLine className="h-3.5 w-3.5 mr-1" />
          Auto-scroll
        </Button>
      </div>

      {/* Log output */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="rounded-md border bg-zinc-950 text-zinc-200 font-mono text-xs p-4 h-[400px] overflow-y-auto"
      >
        {lines.length === 0 && (
          <span className="text-zinc-500">
            {isStreaming ? 'Waiting for logs...' : 'No log output.'}
          </span>
        )}
        {lines.map((line, i) => (
          <div key={i} className="leading-5 whitespace-pre-wrap break-all">
            {line}
          </div>
        ))}
        {isStreaming && lines.length > 0 && (
          <div className="flex items-center gap-2 mt-1 text-zinc-500">
            <Loader2 className="h-3 w-3 animate-spin" />
            streaming...
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Outputs list (compact)
// ---------------------------------------------------------------------------

function OutputsList({ outputs, runId }: { outputs: OutputFile[]; runId: string }) {
  const handleDownload = async (filename: string) => {
    try {
      const { url } = await getOutputDownloadUrl(runId, filename)
      window.open(url, '_blank')
    } catch {
      // ignore
    }
  }

  if (outputs.length === 0) {
    return <p className="text-sm text-muted-foreground">No output files available.</p>
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow className="h-8">
            <TableHead className="text-xs py-1.5">File</TableHead>
            <TableHead className="text-xs py-1.5 w-[100px]">Size</TableHead>
            <TableHead className="w-[50px] py-1.5" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {outputs.map((f) => (
            <TableRow key={f.filename} className="h-8">
              <TableCell className="font-mono text-xs py-1">{f.filename}</TableCell>
              <TableCell className="text-muted-foreground text-xs py-1">
                {formatBytes(f.size)}
              </TableCell>
              <TableCell className="py-1">
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDownload(f.filename)}>
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
